'use client';

import { retrieveCart } from "@lib/data/cart"
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react"

interface CartContextValue {
  cart: any | null
  itemCount: number
  isFetching: boolean
  refreshCart: () => Promise<void>
  setCart: (cart: any | null) => void
}

const CartContext = createContext<CartContextValue | undefined>(undefined)

export const CartProvider = ({ children }: { children: ReactNode }) => {
  const [cart, setCartState] = useState<any | null>(null)
  const [isFetching, setIsFetching] = useState(false)
  const initializedRef = useRef(false)
  const mountedRef = useRef(true)

  const fetchCart = useCallback(async () => {
    if (!mountedRef.current) {
      return
    }
    setIsFetching(true)
    try {
      const data = await retrieveCart()
      if (!mountedRef.current) {
        return
      }
      setCartState(data ?? null)
    } catch (error) {
      console.error('Failed to retrieve cart', error)
      if (mountedRef.current) {
        setCartState(null)
      }
    } finally {
      if (mountedRef.current) {
        setIsFetching(false)
      }
    }
  }, [])

  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
    }
  }, [])

  useEffect(() => {
    if (!initializedRef.current) {
      initializedRef.current = true
      fetchCart()
    }
  }, [fetchCart])

  const setCart = useCallback(
    (next: any | null) => {
      if (!mountedRef.current) {
        return
      }
      setCartState(next)
    },
    [setCartState]
  )

  const value = useMemo<CartContextValue>(() => {
    const itemCount = (cart?.items || []).reduce(
      (sum: number, item: any) => sum + (item?.quantity || 0),
      0
    )

    return {
      cart,
      itemCount,
      isFetching,
      refreshCart: fetchCart,
      setCart,
    }
  }, [cart, fetchCart, isFetching, setCart])

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>
}

export const useCart = () => {
  const context = useContext(CartContext)
  if (!context) {
    throw new Error('useCart must be used within a CartProvider')
  }
  return context
}
